import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HamButtonComponent } from '@modeso/aumico-components';
import { Moment } from 'moment';

@Component({
    selector: 'date-filter-drop-down',
    templateUrl: './date-filter-drop-down.component.html',
    styleUrls: ['./date-filter-drop-down.component.scss'],
})
export class DateFilterDropDown implements OnInit, OnChanges {
    filterOptions = [
        { label: 'Overdue', value: 'overdue' },
        { label: 'Today', value: 'today' },
        { label: 'Next 7 days', value: 'next7' },
        { label: 'Next 14 days', value: 'next14' },
        { label: 'Next 30 days', value: 'next30' },
        { label: 'Custom', value: 'custom' },
    ];

    openedFlyout = false;

    flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };

    filterOptionSelected: string = '';

    showCustomRange = false;
    customStartDate: Date | null = null;
    customEndDate: Date | null = null;
    today = new Date();

    @Input() selectedDate: { startDate: Date | null; endDate: Date | null } | undefined;
    @Input() selectedDates: Array<{ startDate: Date | string | null; endDate: Date | string | null }> | undefined;
    @Input() showDateRangePickerOnly = false;
    @Input() pickDay = false;
    @Input() multiSelect = false;
    @Input() translationKeyFilterName: string = 'aumico_pm_tasks_due_date_filter_label';

    @Output() filterChange = new EventEmitter<{ startDate: Date | null; endDate: Date | null }>();
    @Output() filterChangeMulti = new EventEmitter<Array<{ startDate: Date | null; endDate: Date | null }>>();

    selectedOptions = new Set<string>();

    private lastButtonElement: HTMLElement | undefined;

    ngOnInit(): void {
        if (this.multiSelect && this.selectedDates) {
            this.syncSelectedOptionsFromRanges(this.selectedDates);
            return;
        }

        if (this.selectedDate) {
            this.customStartDate = this.selectedDate.startDate;
            this.customEndDate = this.selectedDate.endDate;

            if (this.multiSelect && (this.customStartDate || this.customEndDate)) {
                this.selectedOptions.add('custom');
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.multiSelect && changes['selectedDates']) {
            const currentRanges = changes['selectedDates'].currentValue as
                | Array<{ startDate: Date | string | null; endDate: Date | string | null }>
                | undefined;
            if (currentRanges) {
                this.syncSelectedOptionsFromRanges(currentRanges);
            } else {
                this.selectedOptions.clear();
            }
            return;
        }

        if (changes['selectedDate'] && changes['selectedDate'].currentValue) {
            const startDate = changes['selectedDate'].currentValue.startDate;
            const endDate = changes['selectedDate'].currentValue.endDate;

            this.customStartDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
            this.customEndDate = typeof endDate === 'string' ? new Date(endDate) : endDate;

            if (this.multiSelect) {
                if (this.customStartDate || this.customEndDate) {
                    this.selectedOptions.add('custom');
                } else {
                    this.selectedOptions.delete('custom');
                }
            }

            if (this.customStartDate && this.customEndDate) {
                const startDate = this.getStartOfDayLocal(this.customStartDate);
                const endDate = this.getEndOfDayLocal(this.customEndDate);

                const today = this.getStartOfDayLocal(new Date());

                if (endDate < today && !startDate) {
                    this.filterOptionSelected = 'overdue';
                } else if (
                    startDate.getTime() === today.getTime() &&
                    endDate.getTime() === this.getEndOfDayLocal(new Date()).getTime()
                ) {
                    this.filterOptionSelected = 'today';
                } else {
                    const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) - 1;
                    if (diffDays === 7) {
                        this.filterOptionSelected = 'next7';
                    } else if (diffDays === 14) {
                        this.filterOptionSelected = 'next14';
                    } else if (diffDays === 30) {
                        this.filterOptionSelected = 'next30';
                    } else {
                        this.filterOptionSelected = 'custom';
                    }
                }
            } else if (!this.customStartDate && this.customEndDate) {
                this.filterOptionSelected = 'overdue';
            } else {
                this.filterOptionSelected = '';
            }
        }
    }

    openFlyout(buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;
        if (!buttonElement) return;

        this.lastButtonElement = buttonElement;
        this.openedFlyout = true;
        this.showCustomRange = false;

        setTimeout(() => {
            const flyoutElement = document.querySelector('.date-picker-flyout-content');
            if (!flyoutElement) return;

            this.calculateFlyoutPosition(buttonElement, flyoutElement);
        }, 0);
    }

    closeFlyout(): void {
        this.openedFlyout = false;
        this.showCustomRange = false;
        this.lastButtonElement = undefined;
    }

    openDateRangePicker(buttonComponent: HamButtonComponent): void {
        const buttonElement = buttonComponent.nativeButton?.nativeElement;
        if (!buttonElement) return;

        this.showCustomRange = true;
        this.openedFlyout = false;

        setTimeout(() => {
            const flyoutElement = document.querySelector('.flyout-custom-date-picker-content');
            if (!flyoutElement) return;

            this.calculateFlyoutPosition(buttonElement, flyoutElement);
        }, 0);
    }

    private calculateFlyoutPosition(buttonElement: HTMLElement, flyoutElement: Element): void {
        const buttonRect = buttonElement.getBoundingClientRect();
        const flyoutRect = flyoutElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = buttonRect.bottom + 8;
        let left = buttonRect.left;

        // Adjust vertical position if it overflows the bottom
        if (top + flyoutRect.height > viewportHeight) {
            top = buttonRect.top - flyoutRect.height - 8;
        }

        // Prevent from going off-screen at the top
        if (top < 0) {
            top = 8;
        }

        // Adjust horizontal position if it overflows on the right
        if (left + flyoutRect.width > viewportWidth) {
            left = viewportWidth - flyoutRect.width - 8;
        }

        // Adjust horizontal position if it overflows on the left
        if (left < 0) {
            left = 8;
        }

        this.flyoutPosition = {
            top: top,
            left: left,
            right: 0, // right is not used, left positioning is sufficient
        };
    }

    onFilterChange(value: string) {
        if (value === 'custom') {
            this.openedFlyout = false;
            this.showCustomRange = true;
            if (this.lastButtonElement) {
                setTimeout(() => {
                    const flyoutElement = document.querySelector('.flyout-custom-date-picker-content');
                    if (flyoutElement && this.lastButtonElement) {
                        this.calculateFlyoutPosition(this.lastButtonElement, flyoutElement);
                    }
                }, 0);
            }
            return;
        }

        const range = this.getRangeForOption(value);
        const startDate = range?.startDate ?? null;
        const endDate = range?.endDate ?? null;
        this.showCustomRange = false;
        this.filterOptionSelected = value;
        this.filterChange.emit({ startDate, endDate });
    }

    onFilterToggle(value: string) {
        if (value === 'custom') {
            if (this.selectedOptions.has('custom')) {
                this.selectedOptions.delete('custom');
                this.customStartDate = null;
                this.customEndDate = null;
                this.showCustomRange = false;
                return;
            }

            this.selectedOptions.add('custom');
            this.openedFlyout = false;
            this.showCustomRange = true;
            if (this.lastButtonElement) {
                setTimeout(() => {
                    const flyoutElement = document.querySelector('.flyout-custom-date-picker-content');
                    if (flyoutElement && this.lastButtonElement) {
                        this.calculateFlyoutPosition(this.lastButtonElement, flyoutElement);
                    }
                }, 0);
            }
            return;
        }

        if (this.selectedOptions.has(value)) {
            this.selectedOptions.delete(value);
        } else {
            this.selectedOptions.add(value);
        }

        this.showCustomRange = false;
    }

    addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    getStartOfDayLocal(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    getEndOfDayLocal(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    }

    setStartDate(m: Moment) {
        // Build UTC midnight for that calendar day
        const year = m.year();
        const month = m.month(); // 0-based
        const dday = m.date();

        const asUtc = new Date(Date.UTC(year, month, dday, 0, 0, 0, 0));
        this.customStartDate = asUtc;
        // Optional: reset end if it's before start
        if (this.customEndDate && this.customEndDate < asUtc) {
            this.customEndDate = null;
        }

        this.applyCustomRange();
    }

    setEndDate(m: Moment) {
        // Build UTC midnight for that calendar day
        const year = m.year();
        const month = m.month(); // 0-based
        const dday = m.date();

        const asUtc = new Date(Date.UTC(year, month, dday, 0, 0, 0, 0));
        this.customEndDate = asUtc;
        this.applyCustomRange();
    }

    selectStartDateToday() {
        const today = new Date();
        this.customStartDate = this.getStartOfDayLocal(today);
        this.applyCustomRange();
    }

    selectEndDateToday() {
        const today = new Date();
        this.customEndDate = this.getStartOfDayLocal(today);
        this.applyCustomRange();
    }

    clearCustomDates(): void {
        this.customStartDate = null;
        this.customEndDate = null;

        if (this.multiSelect) {
            this.selectedOptions.clear();
            this.filterOptionSelected = '';
            this.showCustomRange = false;
            this.filterChangeMulti.emit([]);
            return;
        }

        this.applyCustomRange();
        this.closeFlyout();
    }

    applyCustomRange() {
        if (this.multiSelect) {
            if (this.customStartDate || this.customEndDate) {
                this.selectedOptions.add('custom');
            }
            return;
        }

        if (this.pickDay && this.customStartDate) {
            const range = this.getRangeForOption('custom');
            const startDate = range?.startDate ?? null;
            const endDate = range?.endDate ?? null;
            this.filterOptionSelected = 'custom';
            this.filterChange.emit({ startDate, endDate });
            return;
        }
        if (this.customStartDate && this.customEndDate) {
            this.filterOptionSelected = 'custom';
            const range = this.getRangeForOption('custom');
            const startDate = range?.startDate ?? null;
            const endDate = range?.endDate ?? null;
            this.filterChange.emit({ startDate, endDate });
        }

        if (this.customStartDate == null && this.customEndDate == null) {
            // If both dates are cleared, emit null to reset filter
            this.filterOptionSelected = '';
            this.filterChange.emit({ startDate: null, endDate: null });
        }
    }

    applyMultiSelection(): void {
        if (!this.multiSelect) {
            return;
        }

        this.openedFlyout = false;
        this.showCustomRange = false;
        this.emitMultiSelection();
    }

    isOptionSelected(value: string): boolean {
        return this.multiSelect ? this.selectedOptions.has(value) : this.filterOptionSelected === value;
    }

    hasSelection(): boolean {
        return this.multiSelect ? this.selectedOptions.size > 0 : !!this.filterOptionSelected;
    }

    private emitMultiSelection(): void {
        const ranges: Array<{ startDate: Date | null; endDate: Date | null }> = [];

        this.selectedOptions.forEach((value) => {
            const range = this.getRangeForOption(value);
            if (range) {
                ranges.push(range);
            }
        });
        this.filterChangeMulti.emit(ranges);
    }

    private syncSelectedOptionsFromRanges(
        ranges: Array<{ startDate: Date | string | null; endDate: Date | string | null }>,
    ): void {
        this.selectedOptions.clear();
        this.customStartDate = null;
        this.customEndDate = null;

        const normalizedRanges = ranges.map((range) => ({
            startDate: range.startDate ? new Date(range.startDate) : null,
            endDate: range.endDate ? new Date(range.endDate) : null,
        }));

        const unmatchedRanges: Array<{ startDate: Date | null; endDate: Date | null }> = [];

        normalizedRanges.forEach((range) => {
            let matched = false;

            this.filterOptions.forEach((option) => {
                if (option.value === 'custom') {
                    return;
                }

                const optionRange = this.getRangeForOption(option.value);
                if (optionRange && this.rangesMatch(optionRange, range)) {
                    this.selectedOptions.add(option.value);
                    matched = true;
                }
            });

            if (!matched) {
                unmatchedRanges.push(range);
            }
        });

        if (unmatchedRanges.length > 0) {
            const [customRange] = unmatchedRanges;
            this.customStartDate = customRange.startDate;
            this.customEndDate = customRange.endDate;
            this.selectedOptions.add('custom');
        }
    }

    private rangesMatch(
        left: { startDate: Date | null; endDate: Date | null },
        right: { startDate: Date | null; endDate: Date | null },
    ): boolean {
        const leftStart = left.startDate ? left.startDate.getTime() : null;
        const leftEnd = left.endDate ? left.endDate.getTime() : null;
        const rightStart = right.startDate ? right.startDate.getTime() : null;
        const rightEnd = right.endDate ? right.endDate.getTime() : null;

        return leftStart === rightStart && leftEnd === rightEnd;
    }

    private getRangeForOption(value: string): { startDate: Date | null; endDate: Date | null } | null {
        switch (value) {
            case 'overdue': {
                const today = new Date();
                const startOfToday = this.getStartOfDayLocal(today);
                return { startDate: null, endDate: new Date(startOfToday.getTime() - 1) };
            }
            case 'today': {
                const today = new Date();
                return { startDate: this.getStartOfDayLocal(today), endDate: this.getEndOfDayLocal(today) };
            }
            case 'next7': {
                const today = new Date();
                const startDate = this.getStartOfDayLocal(today);
                const future = new Date(today);
                future.setDate(future.getDate() + 7);
                return { startDate, endDate: this.getEndOfDayLocal(future) };
            }
            case 'next14': {
                const today = new Date();
                const startDate = this.getStartOfDayLocal(today);
                const future = new Date(today);
                future.setDate(future.getDate() + 14);
                return { startDate, endDate: this.getEndOfDayLocal(future) };
            }
            case 'next30': {
                const today = new Date();
                const startDate = this.getStartOfDayLocal(today);
                const future = new Date(today);
                future.setDate(future.getDate() + 30);
                return { startDate, endDate: this.getEndOfDayLocal(future) };
            }
            case 'custom': {
                if (this.pickDay && this.customStartDate) {
                    const startDate = new Date(this.customStartDate);
                    startDate.setHours(0, 0, 0, 0);
                    return { startDate, endDate: startDate };
                }

                if (this.customStartDate && this.customEndDate) {
                    const startDate = new Date(this.customStartDate);
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(this.customEndDate);
                    endDate.setHours(23, 59, 59, 999);
                    return { startDate, endDate };
                }

                return null;
            }
            default:
                return null;
        }
    }
}
